import { useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";

import { CommsRail, type CommsView } from "../CommsRail";
import { DraftList } from "../DraftList";
import { ThreadList } from "../ThreadList";
import { ThreadSearch } from "../ThreadSearch";
import { toThreadSummary, type ThreadSummary } from "../../threadSummary";

import type { DraftSummary } from "../../draftSummary";
import type { CorrespondenceDraft, CorrespondenceThread } from "@platform/api";

export interface CorrespondencePanelProps {
  threads: CorrespondenceThread[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  onCompose: () => void;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;

  /**
   * The unsent replies on this RFP, for the rail's drafts listing.
   *
   * Taken rather than fetched, like the threads: the host app owns the
   * request and this library stays renderable without a query client.
   */
  drafts?: CorrespondenceDraft[];
  isLoadingDrafts?: boolean;
  isDraftsError?: boolean;
  /** Throws a draft away. Absent hides the control. */
  onDiscardDraft?: (draft: DraftSummary) => void;
  /** The draft currently being discarded. */
  discardingDraftId?: string;
  /**
   * The conversation to show under the expanded row.
   *
   * Passed through to the list rather than resolved here: the panel owns
   * which row is open, and the host app owns reading what is in it. The
   * second argument closes the row.
   */
  renderThreadBody?: (
    threadId: string,
    collapse: () => void,
  ) => React.ReactNode;
}

/**
 * The drawer's body: the rail beside whichever listing it selects.
 *
 * Takes threads rather than fetching them, so the drawer renders in Storybook
 * and in tests without a query client, and the host app owns the deadline,
 * the cache key and the paging.
 */
export const CorrespondencePanel = ({
  threads,
  isLoading,
  isError,
  onRetry,
  onCompose,
  onLoadMore,
  isLoadingMore,
  renderThreadBody,
  drafts = [],
  isLoadingDrafts,
  isDraftsError,
  onDiscardDraft,
  discardingDraftId,
}: CorrespondencePanelProps): React.JSX.Element => {
  const [view, setView] = useState<CommsView>("messages");
  const [search, setSearch] = useState("");
  const [expandedThreadId, setExpandedThreadId] = useState<string>();

  const summaries = useMemo(
    () => threads.map((thread) => toThreadSummary(thread)),
    [threads],
  );

  const visible = useMemo(
    () => matching(summaries, search),
    [summaries, search],
  );

  return (
    <Box sx={{ display: "flex", width: "100%", minHeight: 0 }}>
      <CommsRail
        view={view}
        onSelectView={setView}
        onCompose={onCompose}
        // Threads with mail waiting, not messages: the rail says how many
        // conversations need the analyst, which is what the badge in the
        // design counts.
        messageCount={summaries.filter((t) => t.unreadCount > 0).length}
        // Threads carrying an unsent reply. Neither badge is a prop: both are
        // the rows already loaded, so passing either would be a second place
        // to disagree with what the list shows.
        draftCount={summaries.filter((t) => t.hasDraft).length}
      />

      <Stack sx={{ flexGrow: 1, minWidth: 0 }}>
        {view === "drafts" ? (
          <DraftList
            drafts={drafts}
            isLoading={isLoadingDrafts}
            isError={isDraftsError}
            onDiscard={onDiscardDraft}
            discardingId={discardingDraftId}
            // Opening a draft's thread leaves the drafts listing for the
            // conversation it answers, which is where the draft can be read
            // whole. The rail keeps the way back.
            onOpenThread={(threadId) => {
              setView("messages");
              setExpandedThreadId(threadId);
            }}
          />
        ) : (
          <>
            <ThreadSearch value={search} onChange={setSearch} />
            <ThreadList
              threads={visible}
              expandedThreadId={expandedThreadId}
              onToggleThread={(threadId) =>
                setExpandedThreadId((current) =>
                  current === threadId ? undefined : threadId,
                )
              }
              isLoading={isLoading}
              isError={isError}
              onRetry={onRetry}
              isFiltered={search.trim().length > 0}
              onLoadMore={onLoadMore}
              isLoadingMore={isLoadingMore}
              renderThreadBody={renderThreadBody}
            />
          </>
        )}
      </Stack>
    </Box>
  );
};

/**
 * Subject, counterparty and every participant address, case-insensitively.
 *
 * The addresses are searched as well as the label because the label loses
 * them: a participant the directory named shows as `Dana Ruiz`, and matching
 * only that would make `guardian.com` find a carrier exactly when nobody had
 * named them.
 *
 * Message bodies are not searched: a listing carries no bodies, and reading
 * every thread to search them would be one request per row.
 */
const matching = (
  threads: ThreadSummary[],
  search: string,
): ThreadSummary[] => {
  const term = search.trim().toLowerCase();

  if (!term) return threads;

  return threads.filter(
    (thread) =>
      thread.subject.toLowerCase().includes(term) ||
      thread.counterparty.toLowerCase().includes(term) ||
      thread.addresses.some((address) => address.includes(term)),
  );
};
